/**
 * DeveloperMode — client-only feature flag that toggles the Design Workspace
 * (and any future dev-only surfaces) into the Owner sidebar.
 *
 * Persisted under `hbf_dev_mode_v1`. Toggle from the Settings page (System
 * Preferences section) or by pressing Ctrl+Shift+D on any Owner page.
 * Zero backend surface; purely a UI affordance.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const LS_KEY = "hbf_dev_mode_v1";
const DeveloperModeContext = createContext({ devMode: false, toggle: () => {}, setDevMode: () => {} });

export function DeveloperModeProvider({ children }) {
  const [devMode, setDevMode] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, devMode ? "1" : "0"); } catch { /* ignore */ }
  }, [devMode]);

  const toggle = useCallback(() => setDevMode((v) => !v), []);

  // Global hotkey — Ctrl+Shift+D toggles.
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
        e.preventDefault();
        setDevMode((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <DeveloperModeContext.Provider value={{ devMode, toggle, setDevMode }}>
      {children}
    </DeveloperModeContext.Provider>
  );
}

export const useDeveloperMode = () => useContext(DeveloperModeContext);
